import { useState } from "react";
import axios from "axios";
import { Link, redirect, useNavigate, useLoaderData } from "react-router-dom";
import { Header } from "../layout/header.js";
import { Footer } from "../layout/footer.js";
import { faTableList, faQuoteLeft, faUpRightFromSquare, faChevronDown, faCode } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library as fontAwesomeLibrary } from "@fortawesome/fontawesome-svg-core";
import { MissionBanner } from "../components/missionbanner.js";
import { FolderTabs } from "../components/foldertabs.js";
import { MissionSoftwarePanel } from "../components/missionsoftwarepanel.js";
import { useMissionSoftware } from "./missionsoftware_utils.js";
import { CopyToClipboardButton } from "../components/copytoclipboardbutton.js";

import { ImageViewModalWithZoom } from "../components/imageviewmodalwithzoom.js";
import { SearchBar } from "../components/searchbar.js";
import { SearchResultsFacet } from "../components/searchresultsfacet.js";
import { SearchResultsTable } from "../components/searchresultstable.js";
import { ExpandableText } from "../components/expandabletext.js";
import { parseFacetRestricts } from "./searchpage_utils.js";


/**
 * React-router-dom Loader function to load backend endpoint with
 * AJAX, the response from which drives the page.
 *
 * @param params Parameters of the page URL
 */
export async function loader({ params }) {
  let pageNum = parseInt(params.pageNum);
  if (isNaN(pageNum)) {
    pageNum = 1;
  }

  try {
    const requestStart = Date.now();
    const getParams = new URLSearchParams(window.location.search);
    const scrollToTop = (getParams.get("scrollToTop") || "").toString() !== "0";
    const rawFacetRestricts = params.rawFacetRestricts || "";
    const response = await axios.get(
      `/heliodata/app/load_mission_page/${params.missionId}/${pageNum}?${rawFacetRestricts}`
    );
    const requestTimeToComplete = (Date.now() - requestStart) / 1000;


    return {
      missionData: response.data,
      missionId: params.missionId,
      pageNum: pageNum,
      rawFacetRestricts: rawFacetRestricts,
      scrollToTop: scrollToTop,
      requestTimeToComplete,
    };
  }
  catch (error) {
    return redirect("/error");
  }
}


/**
 * React component to display a Mission page.
 *
 * @param props.missionData Backend response describing the mission.
 * @param props.missionId ID of the mission in the heliodata system.
 * @param props.pageNum Page number to display, which may be undefined.
 * @param props.rawFacetResricts Facet restrictions from the page URL,
 * which may be undefined.
 * @return JSX component
 */
export function MissionPageContent(props) {
  const {
    missionData,
    missionId,
    pageNum,
    rawFacetRestricts,
    scrollToTop,
    requestTimeToComplete,
  } = useLoaderData();
  const searchData = missionData.searchData;
  const navigate = useNavigate();
  const facetRestricts = parseFacetRestricts(rawFacetRestricts);
  const [currentQuery, setCurrentQuery] = useState("");
  const [selectedDatasetTab, setSelectedDatasetTab] = useState("datasets");
  const missionDisplayName = missionData.shortName || missionData.longName;
  const software = useMissionSoftware(missionId);
  const softwareLabel = software.status === "ready" && software.tools.length > 0
    ? `Software (${software.tools.length})`
    : "Software";
  const datasetsLabel = missionData.datasetCount > 0
    ? `Datasets (${missionData.datasetCount})`
    : "Datasets";
  const datasetTabs = [
    {key: "datasets", label: datasetsLabel, icon: faTableList},
    {key: "software", label: softwareLabel, icon: faCode},
    {key: "citation", label: "Cite the Mission", icon: faQuoteLeft},
  ];

  // Override query string scroll to top; fixes bug where it is sometimes
  // not respected
  let scrollToTopFixed = scrollToTop;

  if (pageNum !== 1) {
    scrollToTopFixed = false;
  }
  if (Object.keys(rawFacetRestricts).length !== 0) {
    scrollToTopFixed = false;
  }


  // Callback function for a search being made
  const searchCurrentQuery = () => {
    if (currentQuery && currentQuery.length > 0) {
      let adjustedQuery = `${currentQuery} mission:"${missionData.hcutId}"`;
      const safeQuery = encodeURIComponent(adjustedQuery);
      if (safeQuery.length > 0) {
        navigate(`/search/${safeQuery}/1`);
      }
    }
  };

  return (
    <div>
      {/* Page header. Don't scroll to top unless this is the first entry
             point to this page (e.g. not selecteding facets) */}
      <Header wide={false} scrollToTop={scrollToTopFixed} />

      {/* Banner of the page */}
      <MissionBanner
        shortName={missionData.shortName}
        longName={missionData.longName}
        missionStatus={missionData.missionStatus}
        launchDate={missionData.launchDate}
        missionId={missionId}
        breadcrumbLevels={[
          { link: `/missionlist`, text: "Missions and Observatories" },
          { link: `/mission/${missionData.hcutId}`, text: missionData.longName },
        ]}
      />

      <div className="pageContainer pageContainerWide">
        {missionData.layout === 'new' ? (
          <MissionNewLayout missionData={missionData} missionId={missionId} setSelectedDatasetTab={setSelectedDatasetTab} />
        ) : (
          <div className="mission-page-body">
            {/* Mission description */}
            <div className="mission-page-description">
              <strong>Description:</strong><br />
              <ExpandableText text={missionData.description} showWordCount={500} showLineCount={4} />
            </div>

            {/* Information box floating on the right */}
            <MissionInfoBox
              missionData={missionData}
              missionId={missionId}
            />
          </div>
        )}
      </div>

      <div id="missionDatasetsAnchor" className="mission-datasets-header">
        Browse Data and Publications for {missionData.shortName || missionData.longName}
      </div>

      {/* Tabs: Datasets | Software | Cite the Mission */}
      <FolderTabs
        idPrefix="missionDatasets"
        tabs={datasetTabs}
        selected={selectedDatasetTab}
        onSelect={setSelectedDatasetTab}
        panelClassName="mission-datasets-tab-content"
        scrollToId="missionDatasetsAnchor">
        {selectedDatasetTab === "datasets" && (
          <div className="mission-page-datasets-container">
            <div className="mission-page-datasets-left">
              {/* Facets sidebar */}
              <SearchResultsFacet
                facetData={searchData.facets}
                facetRestricts={facetRestricts}
                numFound={searchData.numFound}
                skipMissionsFacet={true}
                forceAlphaSort={true}
                customTitle={"Refine Datasets"}
                navigate={navigate}
                regionTaxonomyTitles={missionData.regionTaxonomyTitles}
                getNavigateUrl={
                  newQueryString =>
                    `/mission/${missionData.hcutId}/1/${newQueryString}?scrollToTop=0`}
              />
            </div>

            <div className="mission-page-datasets-right">
              {/* Search bar for searching mission's datasets */}
              {searchData && searchData.numFound > 0 &&
                <div className="mission-page-datasets-search-container">
                  <div className="mission-page-datasets-search">
                    <SearchBar
                      currentQuery={currentQuery}
                      setCurrentQuery={setCurrentQuery}
                      searchCurrentQuery={searchCurrentQuery}
                      navigate={navigate}
                      noControls={true}
                      whiteBg={true}
                    />
                  </div>
                </div>
              }

              {/* Supplementary info about the instrument, if selected */}
              <div className="mission-page-datasets-supplementary-info">
                <SearchSupplementaryInfo
                  selectedInstrument={facetRestricts.instruments_cs}
                  missionData={missionData}
                  key={facetRestricts.instruments_cs}
                />
              </div>

              {/* Table of datasets, using the search results component */}
              <SearchResultsTable
                searchData={searchData}
                query={""}
                rawFacetRestricts={rawFacetRestricts}
                url={`/heliodata/app/load_mission_page`}
                pageNum={pageNum}
                missionId={missionId}
                requestTimeToComplete={requestTimeToComplete}
                bottomVariant={true}
              />
            </div>
          </div>
        )}

        {selectedDatasetTab === "software" && (
          <div className="mission-datasets-tab-content">
            <MissionSoftwarePanel missionId={missionId} missionName={missionDisplayName} />
          </div>
        )}

        {selectedDatasetTab === "citation" && (
            <div className="missionCitationContent">
              <GetAboutTheMissionCitationContents missionData={missionData} />
            </div>
        )}
      </FolderTabs>
      <Footer />
    </div>
  );
}


/**
 * Displays the info box on the right of the mission page.
 *
 * @param props.missionData The backend response that describes the mission.
 * @param props.missionId ID of the mission in the heliodata system.
 * @return JSX component
 */
function MissionInfoBox(props) {
  const { missionData, missionId } = props;
  const missionLinkContent = [];

  fontAwesomeLibrary.add(faUpRightFromSquare);

  // Collect links to each mission as items for the info box
  missionData.missionLinks.forEach(
    (missionLink, i) => {
      missionLinkContent.push(
        <div className={`mission-glance-container mission-glance-container${i % 2}`} key={missionLink.url + missionLink.name}>
          <div className="mission-glance-left">
            {missionLink.name}
          </div>
          <div className="mission-glance-right">
            <a href={missionLink.url}
              target="_blank"
              rel="noreferrer">
              {missionLink.url}&nbsp;
              <FontAwesomeIcon
                style={{ fontSize: "11px" }}
                icon={faUpRightFromSquare} />
            </a>
          </div>
        </div>
      );
    }
  );

  // Build JSX for the thumbnail image, hidden if it doesn't exist.
  const [hideThumbnail, setHideThumbnail] = useState(false);
  const imageUrl = `/heliodata/app/load_mission_image/${missionId}`;

  const thumbnailContent = !hideThumbnail && (
    <ImageViewModalWithZoom
      src={imageUrl}
      alt="Mission Thumbnail"
      className="mission-glance-thumbnail"
      onError={() => setHideThumbnail(true)}
    />
  );

  // Build JSX for egion content.
  const regionContentItems = [];
  const regionKeys = (
    Object
      .keys(missionData.regionCounts)
      .sort((a, b) => missionData.regionCounts[b] - missionData.regionCounts[a])
  );
  const regionNameSet = {};

  regionKeys.forEach(regionKey => {
    const regionName = regionKey.split(".").pop();
    regionNameSet[regionName] = 1;
  });

  Object.keys(regionNameSet).forEach(regionName => {
    regionContentItems.push(
      <li key={regionContentItems.length + 1000}>
        <Link to={`/region/${regionName}`} className="mission-glance-region-list-item">
          {missionData.regionTaxonomyTitles[regionName]}
        </Link>
      </li>
    );
  });

  const regionContent = regionKeys.length === 0 ? "" : (
    <div className="mission-glance-container mission-glance-container1">
      <div className="mission-glance-left">
        Regions
      </div>
      <div className="mission-glance-right">
        {regionContentItems}
      </div>
    </div>
  );

  // Build JSX for first and last available data rows. These are hidden
  // if this information is not available;
  let lastAvailContent = <div />;

  if (missionData.timeLastAvail) {
    lastAvailContent = (
      <div className="mission-glance-container">
        <div className="mission-glance-left">
          Time of Last Avail. Data (approx.)
        </div>
        <div className="mission-glance-right">
          {missionData.timeLastAvail}
        </div>
      </div>
    );
  }

  let launchDateContent = <div />;

  if (missionData.launchDate) {
    launchDateContent = (
      <div className="mission-glance-container">
        <div className="mission-glance-left">
          Launch Date
        </div>
        <div className="mission-glance-right">
          {missionData.launchDate}
        </div>
      </div>
    );
  }

  // Build JSX for Principal Investigator. This is hidden if the information
  // is not available;

  let piInfoContent = <div />;

  if (missionData.piName) {
    piInfoContent = (
      <div className="mission-glance-container">
        <div className="mission-glance-left">
          Principal Investigator
        </div>
        <div className="mission-glance-right">
          {missionData.piEmail ?
            <span>{missionData.piName}&nbsp;
              (<a href={`mailto:${missionData.piEmail}`}>{missionData.piEmail}</a>)</span> :
            <span>{missionData.piName}</span>}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mission-info-box">
        {/* Header of info box */}
        <div className="mission-info-at-a-glance">
          At a Glance
        </div>

        {/* Mission Thumbnail */}
        {thumbnailContent}

        {/* Principal Investigator Name */}
        {piInfoContent}

        {/* RegionsFirst Available Data */}
        {regionContent}

        {/* Last Available Data */}
        {lastAvailContent}

        {/* Launch Date */}
        {launchDateContent}

        {/* Dataset Count */}
        <div className="mission-glance-container mission-glance-container1">
          <div className="mission-glance-left">
            Dataset Count
          </div>
          <div className="mission-glance-right">
            {missionData.datasetCount}
          </div>
        </div>

        <hr noshade="true" />
        {/* Mission Links */}
        {missionLinkContent}

      </div>

    </div>
  );
}


/**
 * Redesigned two-column mission body layout.
 * Rendered when the backend supplies descriptionAbout (new layout flag).
 *
 * @param props.missionData Backend response describing the mission.
 * @param props.missionId ID of the mission in the heliodata system.
 * @return JSX component
 */
// Render [text](url) markdown links within a plain string segment.
function renderLinks(text, keyPrefix) {
  const parts = [];
  const linkPattern = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
  let last = 0;
  let match;
  while ((match = linkPattern.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(
      <a key={`${keyPrefix}-a${match.index}`} href={match[2]} target="_blank" rel="noreferrer">
        {match[1]}
      </a>
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// Render inline markdown: *italic* (which may itself contain links) and links.
function renderWithLinks(text) {
  const parts = [];
  const italicPattern = /\*([^*]+)\*/g;
  let last = 0;
  let match;
  while ((match = italicPattern.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(...renderLinks(text.slice(last, match.index), `p${match.index}`));
    }
    parts.push(
      <em key={`em${match.index}`}>{renderLinks(match[1], `em${match.index}`)}</em>
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(...renderLinks(text.slice(last), "pend"));
  return parts;
}

function MissionNewLayout(props) {
  const { missionData, missionId, setSelectedDatasetTab } = props;
  const [hideThumbnail, setHideThumbnail] = useState(false);
  const imageUrl = `/heliodata/app/load_mission_image/${missionId}`;

  fontAwesomeLibrary.add(faUpRightFromSquare, faChevronDown);

  const scrollToDatasets = () => {
    const el = document.getElementById('missionDatasetsAnchor');
    setSelectedDatasetTab("datasets");
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="missionNewBody">

      {/* ── LEFT COLUMN ── */}
      <div className="missionNewLeft">

        {/* About section — heading row, time of last avail, body text */}
        {missionData.descriptionAbout && (
          <div className="missionDescSection">
            <div className="missionDescSectionHeader">
              <h2 className="missionDescSectionHeading">About {missionData.shortName || missionData.longName}</h2>
              <button className="missionDataButton" onClick={scrollToDatasets}>
                Jump to Data: {missionData.datasetCount} results&nbsp;
                <FontAwesomeIcon icon={faChevronDown} />
              </button>
            </div>
            {/* Image floated top-right so description text wraps around it */}
            {!hideThumbnail && (
              <div className="missionNewImageFloat">
                <ImageViewModalWithZoom
                  src={imageUrl}
                  alt="Mission Thumbnail"
                  className="missionNewImage"
                  onError={() => setHideThumbnail(true)}
                />
                {missionData.imageCaption && (
                  <p className="missionNewImageCaption">
                    {missionData.imageCaption.text}
                  </p>
                )}
              </div>
            )}

            {missionData.descriptionAbout
              .split('\n\n')
              .map((para, i) => (
                <p key={i} className="missionDescIntro">{renderWithLinks(para)}</p>
              ))
            }
            {missionData.timeLastAvail && (
              <div className="missionLastAvailPill">
                Time of Last Available Data: {missionData.timeLastAvail}
              </div>
            )}
          </div>
        )}


      </div>

      {/* ── RIGHT COLUMN ── */}
      <div className="missionNewRight">

        {/* Cards from description_cards.yml */}
        {Array.isArray(missionData.descriptionCards) && missionData.descriptionCards.map(section => (
          <div key={section.heading} className="missionDescSection">
            <h2 className="missionDescSectionHeading">{section.heading}</h2>
            <div className="missionDescCardStack">
              {section.cards.map(card => (
                <div key={card.title} className="missionDescTypeCard">
                  <h3 className="missionDescTypeCardTitle">{card.title}</h3>
                  <ul className="static-data-ul">
                    {card.items.map(item => <li key={item}>{renderWithLinks(item)}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ))}

      </div>

    </div>
  );
}


/**
 * Show supplementary info about the selected facets.
 * Currently just shows info about the instrument.
 *
 * @param props.missionData Backend response describing the mission.
 * @param props.selectedInstrument Current selected instrument;
 * @return JSX element
 */
function SearchSupplementaryInfo(props) {
  const { missionData, selectedInstrument } = props;
  let instInfo = <div />;
  const instHasDescription = !!missionData.instrumentDescriptions[selectedInstrument];

  if (selectedInstrument && instHasDescription) {
    const instDescription = missionData.instrumentDescriptions[selectedInstrument];
    const instShortName = missionData.instrumentShortNames[selectedInstrument];
    const instLongName = missionData.instrumentLongNames[selectedInstrument];

    instInfo = (
      <div className="mission-page-datasets-supp-info-outer">
        <div className="mission-page-datasets-supp-info-inner">

          <span className="mission-page-datasets-supp-info-pill">
            About Instrument or Data Source "{instShortName}"
          </span><br />

          <strong>{instLongName}</strong>
          <ExpandableText
            text={instDescription}
            showWordCount={50}
            showLineCount={3} />
        </div>
      </div>
    );
  }

  return (
    <div>
      {instInfo}
    </div>
  );

}


/**
 * Gets the mission citation tab contents of the "About the Data" section.
 * @param props Response from backend describing mission.
 * @return JSX element
 */
function GetAboutTheMissionCitationContents(props) {
    const { missionData } = props;
    const c = missionData.citation || {};
    const title     = c.title     || missionData.longName || missionData.shortName || null;
    const authors   = c.authors   || missionData.piName   || null;
    const publisher = c.publisher || null;
    const year      = c.year      || null;
    const spaseUrl  = c.url ||
        (missionData.spaseId
        ? missionData.spaseId.replace("spase://", "https://spase-metadata.org/") + ".html"
        : null);
    const visibleContent = [];

    // Add text citation section. The copied text is built from the same
    // values as the rendered excerpt so the two always agree.
    const hasFullCitation = spaseUrl && authors;
    const currentYear = new Date().getFullYear();
    const authorsSentence = authors && authors.endsWith(".") ? authors : `${authors}.`;

    const citationText = (hasFullCitation
          ? [authorsSentence, `"${title}."`, publisher, year && `(${year}).`, spaseUrl]
          : [`"${title}"`, `(${currentYear}).`, spaseUrl])
          .filter(Boolean).join(" ");

    visibleContent.push(
        <div key="citation-text" className="citationBlock">
          <p><strong>The following excerpt can be used to cite this mission:</strong></p>

          <p style={{paddingLeft: "20px"}}>
            {hasFullCitation
             ? <>
                 {authorsSentence} "{title}."
                 <em>{publisher}</em> ({year}).&nbsp;
               </>
             : <>
                 "{title}"
                 ({currentYear}). &nbsp;
               </>}
            <a href={spaseUrl} target="_Blank" rel="noreferrer">{spaseUrl}</a>
          </p>

          <div className="citationActions">
            <CopyToClipboardButton
              text={citationText}
              eventLabel="mission_citation_text_copied__button"
              eventType="missions"
              eventValue={missionData.shortName} />
          </div>
        </div>
    );

    // Add BibTex Section
    let bibTexId = missionData.hcutId.replace(/(_|\.)/g, '');
    const bibTexAuthor = authors || publisher || undefined;

    const lines = [];
    lines.push(`title = {${title}}`);

    if (bibTexAuthor) {
        lines.push(`author = {${bibTexAuthor}}`);
    }

    if (spaseUrl) {
        lines.push(`url = {${spaseUrl}}`);
    }

    lines.push(`year = {${year || currentYear}}`);

    if (publisher) {
        lines.push(`publisher = {${publisher}}`);
    }

    const bibTexText = `@misc{${bibTexId},\n`
          + lines.map((line) => "  " + line).join(",\n")
          + "\n}";

    visibleContent.push(
        <div key="citation-bibtex" className="citationBlock">
              <p><strong>The following BibTeX can also be used to cite this mission:</strong></p>

              <p style={{paddingLeft: "20px"}}>
                <code>
                  @misc&#123;{bibTexId},<br />
                  {lines.map(
                      (line, idx) =>
                          <span key={idx}>
                            {idx > 0 ? <span>,<br /></span> : ""}
                            &nbsp;&nbsp;{line}
                          </span>
                  )}
                  <br />
                  &#125;
                </code>
              </p>

              <div className="citationActions">
                <CopyToClipboardButton
                  text={bibTexText}
                  eventLabel="mission_citation_bibtex_copied__button"
                  eventType="missions"
                  eventValue={missionData.shortName} />
              </div>
        </div>
    );

    return visibleContent;
}
